// Light/dark theming for the admin console.
//
// Three modes are offered: "system" (follow the OS/browser preference — the
// default), "light", and "dark". The chosen mode is persisted in localStorage
// per browser. In "system" mode we keep listening to prefers-color-scheme, so
// a laptop or phone that flips to dark at sunset flips the console with it
// without a reload.
//
// Colors are declared as Vuetify theme colors, which means Vuetify publishes
// each one as a CSS variable holding an RGB triplet — `surface-subtle` becomes
// `--v-theme-surface-subtle: 250,250,250`. Component styles should therefore
// use `rgb(var(--v-theme-surface-subtle))` rather than a literal hex, so they
// follow the active theme. See the token guide below.

import { ref } from "vue";

export const THEME_STORAGE_KEY = "admin_console_theme_mode";

const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";

// Neutral tokens, in rough order of "depth" on the page:
//
//   background      the page behind everything
//   surface         a card / panel sitting on the background
//   surface-subtle  a lightly recessed area inside a card (table stripes,
//                   read-only fields, code blocks)
//   surface-muted   a more strongly recessed area (headers, inactive tabs)
//   surface-inset   wells and gutters that should read as "below" the surface
//   border-subtle   hairline dividers
//   border-strong   emphasised borders and outlines
//   text-muted      secondary/caption text
//
// Note the light and dark ramps run in opposite directions: on light,
// "recessed" is darker than the surface; on dark, it is lighter.

const lightColors = {
  background: "#eeeeee",
  surface: "#ffffff",
  "surface-subtle": "#fafafa",
  "surface-muted": "#f2f2f2",
  "surface-inset": "#e8e8e8",
  "border-subtle": "#e0e0e0",
  "border-strong": "#bdbdbd",
  // AA-tuned (2026-08): muted text ≥4.5:1 on every light surface; the
  // semantic colors carry WHITE text on chips/buttons, so they must sit at
  // ≥4.5:1 against white themselves — the old brighter values (#1976d2,
  // #43a047, #ef6c00, #e53935, #0288d1) ranged 2.8–4.3 and failed the axe
  // gate (e2e_stream_suite admin-ui a11y audit).
  "text-muted": "#666666",
  primary: "#1565c0",
  secondary: "#3f51b5",
  success: "#2e7d32",
  warning: "#b35309",
  error: "#c62828",
  info: "#01579b",

  // Solid-fill variants of the semantic colors — see the dark-theme note.
  // On light they are simply the semantic color itself.
  "primary-fill": "#1565c0",
  "success-fill": "#2e7d32",
  "warning-fill": "#b35309",
  "error-fill": "#c62828",
  "info-fill": "#01579b",

  // Decorative gradient stops for the colored card headers.
  "accent-green-from": "#66bb6a",
  "accent-green-to": "#43a047",
  "accent-purple-from": "#7e57c2",
  "accent-purple-to": "#5e35b1",
  "accent-cyan-from": "#26c6da",
  "accent-cyan-to": "#00acc1",
  "accent-blue-from": "#42a5f5",
  "accent-blue-to": "#1e88e5",
  "accent-teal-from": "#2f8fab",
  "accent-teal-to": "#26758c",
};

const darkColors = {
  background: "#121212",
  surface: "#1e1e1e",
  "surface-subtle": "#252525",
  "surface-muted": "#2c2c2c",
  "surface-inset": "#171717",
  "border-subtle": "#383838",
  "border-strong": "#565656",
  "text-muted": "#a0a0a0",
  // Status colors are lightened for dark backgrounds: the light-theme values
  // are chosen for contrast against white and go muddy against #1e1e1e.
  // These are the TEXT/ICON values — bright enough to read on a dark surface.
  primary: "#64b5f6",
  secondary: "#9fa8da",
  success: "#66bb6a",
  warning: "#ffb74d",
  error: "#ef5350",
  info: "#4fc3f7",

  // ...but the same brightness used as a SOLID FILL glares: #66bb6a sits at
  // ~8:1 against the page, so a "Create New" button reads as a slab of light.
  // (Vuetify compensates by putting black text on it, which looks brighter
  // still.) These darker fills carry white text at ~5:1 while sitting only
  // ~3:1 above the page. Applied to the .bg-* utilities by
  // assets/css/theme-fills.css, which is scoped to the dark theme — so
  // .text-success and <v-icon color="success"> keep the bright value above.
  "primary-fill": "#1565c0",
  "success-fill": "#2e7d32",
  "warning-fill": "#b35309",
  "error-fill": "#c62828",
  "info-fill": "#01579b",

  // Decorative header gradients, muted the same way. White header text goes
  // from ~2:1 on the light-theme stops to ~5:1 on these.
  "accent-green-from": "#2e7d32",
  "accent-green-to": "#1b5e20",
  "accent-purple-from": "#544099",
  "accent-purple-to": "#3f3175",
  "accent-cyan-from": "#00697a",
  "accent-cyan-to": "#004f5c",
  "accent-blue-from": "#1565c0",
  "accent-blue-to": "#0d47a1",
  "accent-teal-from": "#1f6478",
  "accent-teal-to": "#174d5c",
};

export const themes = {
  light: {
    dark: false,
    colors: lightColors,
  },
  dark: {
    dark: true,
    colors: darkColors,
  },
};

function readStoredMode() {
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  return "system";
}

// Reactive so the topbar toggle re-renders when the mode changes.
export const themeMode = ref(readStoredMode());

export function systemPrefersDark() {
  if (!window.matchMedia) {
    return false;
  }
  return window.matchMedia(DARK_MEDIA_QUERY).matches;
}

export function resolveThemeName(mode) {
  if (mode === "light" || mode === "dark") {
    return mode;
  }
  return systemPrefersDark() ? "dark" : "light";
}

// The theme Vuetify should be created with, so the first paint is already
// correct instead of flashing light and then correcting itself.
export function initialThemeName() {
  return resolveThemeName(themeMode.value);
}

let vuetifyTheme = null;

function applyTheme() {
  const name = resolveThemeName(themeMode.value);

  if (vuetifyTheme) {
    vuetifyTheme.global.name.value = name;
  }

  // Tells the browser to render its own chrome — scrollbars, native form
  // controls, spellcheck underlines — to match. Without this you get white
  // scrollbars framing a dark page.
  document.documentElement.style.colorScheme = name;
}

export function setThemeMode(mode) {
  themeMode.value = mode;
  window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  applyTheme();
}

// Called once from main.js with the created Vuetify instance.
export function installTheme(vuetify) {
  vuetifyTheme = vuetify.theme;
  applyTheme();

  if (!window.matchMedia) {
    return;
  }

  // Only matters while the user is on "system", but the listener is cheap and
  // stays attached so switching back to "system" picks up straight away.
  window.matchMedia(DARK_MEDIA_QUERY).addEventListener("change", function () {
    if (themeMode.value === "system") {
      applyTheme();
    }
  });
}
